import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { ApiEnvelope } from '../../core/models';

interface RecoveryReferenceResponse {
  verification_reference: string;
}

interface RecoveryAuthorizationResponse {
  recovery_token: string;
}

@Injectable({ providedIn: 'root' })
export class PasswordRecoveryService {
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly prefix = 'grih_upvan_recovery_';
  private readonly memory = new Map<string, string>();

  request(email: string): Observable<ApiEnvelope<RecoveryReferenceResponse>> {
    this.clear();
    return this.api
      .post<RecoveryReferenceResponse>('/auth/forgot-password', { login: email.trim() })
      .pipe(
        tap((response) => {
          this.write('reference', response.data.verification_reference);
          this.write('masked_email', maskEmail(email));
        }),
      );
  }

  verify(otp: string): Observable<ApiEnvelope<RecoveryAuthorizationResponse>> {
    return this.api
      .post<RecoveryAuthorizationResponse>('/auth/verify-otp', {
        verification_reference: this.reference(),
        otp,
      })
      .pipe(
        tap((response) => {
          this.remove('reference');
          this.write('authorization', response.data.recovery_token);
        }),
      );
  }

  resend(): Observable<ApiEnvelope<null>> {
    return this.api.post<null>('/auth/resend-otp', {
      verification_reference: this.reference(),
    });
  }

  reset(password: string, confirmation: string): Observable<ApiEnvelope<null>> {
    return this.api.post<null>('/auth/reset-password', {
      recovery_token: this.authorization(),
      password,
      password_confirmation: confirmation,
    });
  }

  reference(): string | null {
    return this.read('reference');
  }

  authorization(): string | null {
    return this.read('authorization');
  }

  maskedEmail(): string {
    return this.read('masked_email') ?? 'your email address';
  }

  markComplete(): void {
    this.clear();
    this.write('complete', 'true');
  }

  isComplete(): boolean {
    return this.read('complete') === 'true';
  }

  clear(): void {
    for (const key of ['reference', 'authorization', 'masked_email', 'complete']) {
      this.remove(key);
    }
  }

  private read(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return sessionStorage.getItem(this.prefix + key);
      } catch {
        // Privacy modes may disable session storage; memory still keeps this tab usable.
      }
    }
    return this.memory.get(key) ?? null;
  }

  private write(key: string, value: string): void {
    this.memory.set(key, value);
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(this.prefix + key, value);
      } catch {
        // Use the in-memory fallback without weakening the server-side proof.
      }
    }
  }

  private remove(key: string): void {
    this.memory.delete(key);
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.removeItem(this.prefix + key);
      } catch {
        // Nothing else is required when storage is unavailable.
      }
    }
  }
}

export function maskEmail(email: string): string {
  const [local, domain] = email.trim().toLowerCase().split('@');
  if (!local || !domain) return 'your email address';
  return `${local.charAt(0)}*****@${domain}`;
}
